import type { FilterState, SortKey, SortState } from '@mona-roadmap/domain';
import { ArrowDown, ArrowUp, Filter } from 'lucide-react';
import { useRef, useState, type DragEvent, type PointerEvent } from 'react';
import { PINNED } from '../lib/columnLayout';
import { sortKeyOf, type ColumnDef, type ColumnKey } from '../lib/columns';
import { isColumnActive } from './FilterEditor';

interface Props {
  columns: ColumnDef[];
  filters: FilterState;
  filtersActive: boolean;
  sort: SortState;
  /** Click on a header: sort by that column (again: descending, then back to the default). */
  onSort: (key: SortKey) => void;
  /** Opens the shared filter editor on this column, hanging from the clicked funnel. */
  onOpenFilter: (column: ColumnKey, anchor: HTMLElement) => void;
  onResizeColumn: (key: ColumnKey, px: number) => void;
  /** Double click on a column edge: back to the default width. */
  onResetColumnWidth: (key: ColumnKey) => void;
  /** Drop `key` where `target` is. */
  onMoveColumn: (key: ColumnKey, target: ColumnKey) => void;
}

export function GridHeader({ columns, filters, filtersActive, sort, onSort, onOpenFilter, onResizeColumn, onResetColumnWidth, onMoveColumn }: Props) {
  const [dragKey, setDragKey] = useState<ColumnKey | null>(null);
  const [dropKey, setDropKey] = useState<ColumnKey | null>(null);
  const resizing = useRef(false);
  const dragFrom = dragKey ? columns.findIndex((c) => c.key === dragKey) : -1;

  const endDrag = () => {
    setDragKey(null);
    setDropKey(null);
  };

  return (
    <div role="row" className="tg-row tg-head" aria-label="Column headers">
      {columns.map((c, i) => {
        const active = isColumnActive(c, filters);
        const sortKey = sortKeyOf(c);
        const sorted = sortKey !== null && sort.key === sortKey;
        const movable = c.key !== PINNED;
        const dropSide = dragKey && dropKey === c.key && dragKey !== c.key ? (c.key === PINNED || dragFrom > i ? 'left' : 'right') : undefined;
        return (
          <div
            key={c.key}
            role="columnheader"
            className="tg-cell"
            data-col={c.key}
            data-dragging={dragKey === c.key || undefined}
            data-drop={c.key === PINNED && dropSide ? 'right' : dropSide}
            title={c.hint}
            aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            draggable={movable}
            onDragStart={(e: DragEvent) => {
              if (resizing.current) {
                e.preventDefault();
                return;
              }
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', c.key); // Firefox only starts a drag when it carries data
              setDragKey(c.key);
            }}
            onDragOver={(e: DragEvent) => {
              if (!dragKey) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dropKey !== c.key) setDropKey(c.key);
            }}
            onDrop={(e: DragEvent) => {
              e.preventDefault();
              if (dragKey && dragKey !== c.key) onMoveColumn(dragKey, c.key);
              endDrag();
            }}
            onDragEnd={endDrag}
          >
            {sortKey ? (
              <button type="button" className="th-sort" data-sorted={sorted || undefined} aria-label={`Sort by ${c.label}`} title={`Sort by ${c.label}`} onClick={() => onSort(sortKey)}>
                <span className="truncate">{c.label}</span>
                {sorted ? (sort.dir === 'asc' ? <ArrowUp className="size-3.5 flex-none" aria-hidden /> : <ArrowDown className="size-3.5 flex-none" aria-hidden />) : <ArrowUp className="sort-hint size-3.5 flex-none" aria-hidden />}
              </button>
            ) : (
              <span className="truncate">{c.label}</span>
            )}
            {c.filter ? (
              <button
                type="button"
                className={`grid size-6 flex-none place-items-center rounded hover:bg-hover ${active ? 'text-accent-ink' : 'text-faint'}`}
                aria-label={`Filter ${c.label}${active ? ' (active)' : ''}`}
                data-active={active || undefined}
                onClick={(e) => onOpenFilter(c.key, e.currentTarget)}
              >
                <Filter className="size-3.5" fill={active ? 'currentColor' : 'none'} aria-hidden />
              </button>
            ) : null}
            {c.key === 'progress' && filtersActive ? (
              <span className="rounded bg-accent-soft px-1.5 text-[11.5px] font-medium normal-case tracking-normal text-accent-ink" title="Counts reflect active filters">
                filtered
              </span>
            ) : null}
            <ResizeHandle label={c.label} columnKey={c.key} resizing={resizing} onResize={onResizeColumn} onReset={onResetColumnWidth} />
          </div>
        );
      })}
    </div>
  );
}

/** The draggable right edge of a header cell. Drag to resize, double click to go back to the default width. */
function ResizeHandle({ label, columnKey, resizing, onResize, onReset }: { label: string; columnKey: ColumnKey; resizing: { current: boolean }; onResize: (key: ColumnKey, px: number) => void; onReset: (key: ColumnKey) => void }) {
  const start = useRef<{ x: number; width: number } | null>(null);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    resizing.current = true; // the header cell is draggable: keep this gesture from starting a column drag
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, width: e.currentTarget.parentElement?.getBoundingClientRect().width ?? 0 };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    onResize(columnKey, start.current.width + e.clientX - start.current.x);
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    start.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    // the click that follows a drag must not reach the sort button underneath
    setTimeout(() => (resizing.current = false), 0);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      title="Drag to resize. Double click to reset"
      className="col-resizer"
      data-testid={`resize-${columnKey}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset(columnKey);
      }}
    />
  );
}
